import {
    MCP_OAUTH_AUTHORIZATION_URL_KEY,
    MCP_OAUTH_STARTED_AT_MS_KEY,
} from 'src/constants/sessionStorage';
import { getMCPAuthorizationReturnUrl } from 'src/routes/MCPAuthorization/authorizationReturn';

export const MCP_OAUTH_CONTINUATION_MAX_AGE_MS = 10 * 60 * 1000;

type SessionStorageReader = (key: string) => string | null;
type SessionStorageRemover = (key: string) => void;

const clearMCPAuthorizationContinuation = (removeItem: SessionStorageRemover) => {
    removeItem(MCP_OAUTH_AUTHORIZATION_URL_KEY);
    removeItem(MCP_OAUTH_STARTED_AT_MS_KEY);
};

export const storeMCPAuthorizationContinuation = ({
    search,
    apiHost,
    setItem,
    nowMs,
}: {
    search: string;
    apiHost: string;
    setItem: (key: string, value: string) => void;
    nowMs: number;
}): boolean => {
    const authorizationUrl = getMCPAuthorizationReturnUrl({ search, apiHost });
    if (!authorizationUrl) {
        return false;
    }

    setItem(MCP_OAUTH_AUTHORIZATION_URL_KEY, authorizationUrl);
    setItem(MCP_OAUTH_STARTED_AT_MS_KEY, String(nowMs));
    return true;
};

export const resolveMCPAuthorizationContinuation = ({
    search,
    apiHost,
    getItem,
    nowMs,
}: {
    search: string;
    apiHost: string;
    getItem: SessionStorageReader;
    nowMs: number;
}): string | null => {
    const authorizationUrlFromUrl = getMCPAuthorizationReturnUrl({ search, apiHost });
    if (authorizationUrlFromUrl) {
        return authorizationUrlFromUrl;
    }

    const authorizationUrl = getItem(MCP_OAUTH_AUTHORIZATION_URL_KEY);
    const startedAtMs = Number(getItem(MCP_OAUTH_STARTED_AT_MS_KEY));
    const isFresh =
        Number.isFinite(startedAtMs) &&
        nowMs >= startedAtMs &&
        nowMs - startedAtMs <= MCP_OAUTH_CONTINUATION_MAX_AGE_MS;
    if (!authorizationUrl || !isFresh) {
        return null;
    }

    return getMCPAuthorizationReturnUrl({
        search: `?next=${encodeURIComponent(authorizationUrl)}`,
        apiHost,
    });
};

export const getMCPMagicLinkReturnPath = ({
    search,
    apiHost,
    getItem,
    nowMs,
}: {
    search: string;
    apiHost: string;
    getItem: SessionStorageReader;
    nowMs: number;
}): string | null => {
    const authorizationUrl = resolveMCPAuthorizationContinuation({
        search,
        apiHost,
        getItem,
        nowMs,
    });
    return authorizationUrl ? `/login?next=${encodeURIComponent(authorizationUrl)}` : null;
};

export const getMCPPasswordFreeLink = ({
    search,
    apiHost,
    getItem,
    nowMs,
}: {
    search: string;
    apiHost: string;
    getItem: SessionStorageReader;
    nowMs: number;
}): string | null => {
    const authorizationUrl = resolveMCPAuthorizationContinuation({
        search,
        apiHost,
        getItem,
        nowMs,
    });
    return authorizationUrl
        ? `/password-free-link?next=${encodeURIComponent(authorizationUrl)}`
        : null;
};

export const consumeMCPAuthorizationContinuation = ({
    search,
    apiHost,
    getItem,
    removeItem,
    nowMs,
}: {
    search: string;
    apiHost: string;
    getItem: SessionStorageReader;
    removeItem: SessionStorageRemover;
    nowMs: number;
}): string | null => {
    const authorizationUrl = resolveMCPAuthorizationContinuation({
        search,
        apiHost,
        getItem,
        nowMs,
    });
    clearMCPAuthorizationContinuation(removeItem);
    return authorizationUrl;
};

export { clearMCPAuthorizationContinuation };
